/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Filters, Card, Toast, SkeletonPage, Button, IndexTable, useIndexResourceState, EmptySearchResult, Page } from '@shopify/polaris';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { PlusMinor } from '@shopify/polaris-icons';
import Banners from '../Shared/Banners';
import useDebounce from '../../hooks/useDebounce';
import { stateConverter } from '../FilterEngine/filterParams';
import { handleFilterChange } from '../FilterEngine/FilterEngine';
import TablePagination from '../FilterEngine/TablePagination/TablePagination';
import moment from 'moment';
import NewEventPackageModal from './NewEventPackageModal';

const EventPackages = (props) => {
  const [banner, setBanner] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventPackages, setEventPackages] = useState([]);
  const [lastPage, setLastPage] = useState(null);
  const [showEventPackageModal, setShowEventPackageModal] = useState(false);

  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toggleToastActive = useCallback((message) => {
    setToastActive(!toastActive);
    setToastMessage(message);
  }, [toastActive]);

  const toastMarkup = toastActive ? (
    <Toast content={toastMessage} onDismiss={toggleToastActive} duration={2000} />
  ) : null;

  const { t } = useTranslation();
  const location = useLocation();
  const matchParams = useParams();
  const history = useNavigate();

  const [tableFilters, setTableFilters] = useState(stateConverter(location));
  const debouncedSearch = useDebounce(tableFilters, 400);

  const fetchEventPackages = useCallback(() => {
    const params = {
      per_page: 30,
      page: tableFilters.page,
      search: tableFilters.queryValue,
      q: '',
      s: { sorts: tableFilters.sorts }
    };

    axios.post('/v1/event_packages/search', params)
      .then((res) => {
        setLastPage(res.data.lastPage);
        setEventPackages(res.data.event_packages);
        setLoading(false);
      })
      .catch((err) => {
        setBanner([{ title: t('shared.something_went_wrong'), status: 'critical', details: err.response }]);
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [debouncedSearch]);

  const filters = [];

  const placeHolderText = t('user.search');

  const filterControl = (
    <Filters
      queryValue={tableFilters.queryValue}
      filters={filters}
      onQueryChange={handleFilterChange({ history, location }, setTableFilters, 'queryValue')}
      onQueryClear={handleFilterChange({ history, location }, setTableFilters, 'queryValue', 'reset', '')}
      onClearAll={() => {}}
      appliedFilters={[]}
      queryPlaceholder={placeHolderText}
    />
  );

  const handleProductAccessToggle = () => {
    const params = {
      ids: selectedResources
    };

    axios.post(`/v1/event_packages/delete_all`, params)
      .then(() => {
        toggleToastActive(t('shared.success'));
        setTimeout(() => {
          setBanner([]);
        }, [3000]);
        fetchEventPackages();
      })
      .catch(() => {
        toggleToastActive(t('shared.something_went_wrong'));
        setTimeout(() => {
          setBanner([]);
        }, [3000]);
      });
  };

  useEffect(() => {
    fetchEventPackages();
  }, [fetchEventPackages]);

  const dismissBanner = () => {
    setBanner([]);
  };

  const handleNew = (params) => {
    axios.post(`/v1/event_packages`, params)
      .then(() => {
        fetchEventPackages();
      })
      .catch(() => {});
  };

  let newEventPackageModal = null;

  if (showEventPackageModal) {
    newEventPackageModal = (
      <NewEventPackageModal
        title={t('eventpackages.new_event_package')}
        handleSubmit={handleNew}
        close={() => setShowEventPackageModal(false)}
      />
    );
  }

  const headings = [
    { title: t('user.name') },
    { title: t('client.client') },
    { title: t('user.email') },
    { title: t('comment.comment') },
    { title: t('shared.created_at') },
    { title: t('eventpackages.initial_event_count') },
    { title: t('eventspackages.remaining_event_count') }
  ];

  const resourceName = {
    singular: t('staff.staff'),
    plural: t('staff.staff')
  };

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange
  } = useIndexResourceState(eventPackages);

  const rowMarkup = eventPackages.map(({ id, client, name, note, token, original_value, current_value, created_at }, index) => (
    <IndexTable.Row
      id={id}
      key={id}
      selected={selectedResources?.includes(id)}
      position={index}
    >
      <IndexTable.Cell>
        <Button plain url={`/event_packages/${token}/event_package_transactions`}>{name}</Button>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Button plain url={`/clients/${client.token}`}>{client.name}</Button>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {client.email}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {note}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {moment(created_at).format('LLLL')}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {original_value}
      </IndexTable.Cell>
      <IndexTable.Cell>
        {current_value}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const promotedBulkActions = [
    {
      content: t('shared.delete'),
      onAction: () => handleProductAccessToggle()
    }
  ];

  const emptyStateMarkup = (
    <EmptySearchResult
      title={t('eventpackages.no_event_packages_available')}
      withIllustration
    />
  );

  // eslint-disable-next-line no-unused-vars
  const content = (
    <IndexTable
      resourceName={resourceName}
      headings={headings}
      rows={eventPackages}
      itemCount={eventPackages.length}
      selectedItemsCount={
        allResourcesSelected ? 'All' : selectedResources.length
      }
      onSelectionChange={handleSelectionChange}
      emptyState={emptyStateMarkup}
      promotedBulkActions={promotedBulkActions}
    >
      {rowMarkup}
    </IndexTable>
  );

  return (
    <Page
      title={t('eventpackages.event_packages')}
      fullWidth
      primaryAction={{ icon: PlusMinor, content: t('eventpackages.new_event_package'), onAction: () => setShowEventPackageModal(true)}}
      separator
    >
      <Banners banners={banner} onDismissBanner={dismissBanner} />
      {toastMarkup}
      {newEventPackageModal}
      {!loading ? (
        <Card sectioned>
          <Card.Subsection>
            {filterControl}
          </Card.Subsection>
          <Card.Subsection>
            {content}
          </Card.Subsection>
        </Card>
      ) : <SkeletonPage />}
      <TablePagination
        pageFilter={tableFilters.page}
        setTableFilters={setTableFilters}
        records={eventPackages}
        lastPage={lastPage}
      />
    </Page>
  );
};

export default EventPackages;
